/**
 * AutoOrderCard — per-student auto-order status + entry points on /my-family.
 *
 * One row per LIVE auto-order. A student can hold one subscription per mealtime
 * (a Breakfast one alongside a Lunch one), so a child with two gets two rows,
 * each labelled with its meal and navigating to its own manage page
 * (`/family/auto-orders/:id`). A child with none gets a single "Set up" row into
 * the wizard (`/family/auto-orders/new?studentUserId=`); a child who already has
 * one gets an "Add another" link into the same wizard, where the mealtime step
 * offers whatever else their school serves. Reuses the existing auto-order
 * flows — this card only surfaces + links to them.
 */

import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { Skeleton } from '@ordo/ui/skeleton'
import { AlertCircle, ChevronRight, Plus } from 'lucide-react'
import type { ReactElement } from 'react'
import { useMemo } from 'react'
import { Link } from 'react-router'

import type { MyFamilyMember } from '@/api/family'
import { useFamilySubscriptionsQuery } from '@/api/family-subscriptions'
import { UserAvatar } from '@/components/user-identity-card'
import { memberAcceptsParentOrders, subscriptionAcceptsParentOrders } from '@/lib/family-ordering-access'

import {
  type AutoOrderRowState,
  type AutoOrderTone,
  describeAutoOrder,
  liveAutoOrdersByStudent,
} from './auto-order-helpers'
import { getDisplayName, getInitials } from './helpers'

const TONE_BADGE: Record<AutoOrderTone, 'success' | 'warning' | 'secondary'> = {
  active: 'success',
  paused: 'warning',
  inactive: 'secondary',
}

const ROW_CLASS = 'flex items-center gap-3 rounded-lg border border-border px-3 py-2.5'

export function AutoOrderCard({ members }: { members: MyFamilyMember[] }): ReactElement | null {
  const { data, isPending } = useFamilySubscriptionsQuery()

  const subsByStudent = useMemo(
    () =>
      liveAutoOrdersByStudent(
        (data?.subscriptions ?? []).filter((sub) => subscriptionAcceptsParentOrders(sub, members)),
      ),
    [data, members],
  )

  if (!members.some(memberAcceptsParentOrders)) {
    return null
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Auto-Order</CardTitle>
        <p className="mt-1 text-sm text-muted-foreground">Set recurring meals so ordering runs on autopilot.</p>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        {members.map((member) => {
          const user = member.user
          if (!user || !memberAcceptsParentOrders(member)) {
            return null
          }
          const displayName = getDisplayName(user)
          const avatar = (
            <UserAvatar
              name={displayName}
              initials={getInitials(user)}
              avatarUri={user.avatarUri}
              avatarColor={user.avatarColor}
              size="sm"
            />
          )
          const subs = subsByStudent.get(user.id) ?? []
          const emptyState = describeAutoOrder(null, user.id)

          // Still loading, or nothing set up: the single "Set up" row.
          if (isPending || subs.length === 0) {
            return (
              <div key={member.id} className={ROW_CLASS}>
                {avatar}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">{displayName}</p>
                  {isPending ? <Skeleton className="mt-1 h-4 w-24" /> : <AutoOrderStatus state={emptyState} />}
                </div>
                {!isPending ? (
                  <Button variant="outline" size="sm" className="shrink-0" asChild>
                    <Link to={emptyState.href}>{emptyState.ctaLabel}</Link>
                  </Button>
                ) : null}
              </div>
            )
          }

          return (
            <div key={member.id} className="flex flex-col gap-2">
              {subs.map((sub) => {
                const state = describeAutoOrder(sub, user.id)
                return (
                  <Link key={sub.id} to={state.href} className={`${ROW_CLASS} transition-colors hover:bg-muted/30`}>
                    {avatar}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground">
                        {displayName}
                        {state.mealtimeLabel ? (
                          <span className="text-muted-foreground"> · {state.mealtimeLabel}</span>
                        ) : null}
                      </p>
                      <AutoOrderStatus state={state} />
                    </div>
                    <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
                  </Link>
                )
              })}
              {/* Same wizard, same entry point as "Set up" — it just opens on a
                  child who already has one, so its mealtime step offers the
                  meals this school serves that aren't spoken for yet. */}
              <Button variant="ghost" size="sm" className="self-start" asChild>
                <Link to={emptyState.href}>
                  <Plus className="h-4 w-4" aria-hidden />
                  Add another for {displayName}
                </Link>
              </Button>
            </div>
          )
        })}
        <p className="mt-1 text-xs text-muted-foreground">
          Auto-orders use your default payment method, charged weekly before delivery.
        </p>
      </CardContent>
    </Card>
  )
}

function AutoOrderStatus({ state }: { state: AutoOrderRowState }): ReactElement {
  return (
    <>
      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
        <Badge variant={TONE_BADGE[state.tone]}>{state.status}</Badge>
        {state.schedule ? <span className="text-xs text-muted-foreground">{state.schedule}</span> : null}
      </div>
      {state.warning ? (
        <p className="mt-1 flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />
          {state.warning}
        </p>
      ) : null}
    </>
  )
}
